// Updated TenderTable.js with full LeadsTable parity
import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, TextField, Select, MenuItem, InputLabel, FormControl,
  IconButton, Dialog, DialogTitle, DialogContent, Grid, Checkbox, Button, Popover,
  FormControlLabel, TableContainer, Paper, InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import '@fontsource/montserrat';
import { useAuth } from './AuthContext';
import LoadingOverlay from './LoadingOverlay'; // Adjust path if needed
import { CRM_TABLE_SX, latestCrmRows, newestCrmRows } from '../utils/crmTableUtils';

// ✅ ADDED: MUI Date Picker imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const THIRD_PARTY_STATUS = 'Third Party Transfer';
const THIRD_PARTY_FIELDS = [
  'Third Party Name',
  'Third Party Address',
  'Third Party GST Number',
  'Third Party Certificate Validity',
  'Approved By',
  'Authorization Certificate'
];
const AUTH_CERT_FIELD = 'Authorization Certificate';
const MAX_UPLOAD_BYTES = 1_200_000;

const theme = createTheme({
  typography: {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 10.5
  }
});

const selectorStyle = {
  fontFamily: 'Montserrat, sans-serif',
  fontSize: 8
};

// ✅ ADDED: small table styling for logs (simple + readable)
const logTableSx = {
  '& .MuiTableCell-root': {
    fontSize: 'var(--crm-table-font-size, 10px)',
    padding: '6px 8px',
    verticalAlign: 'top'
  }
};

const TenderTable = () => {
  const { user } = useAuth();
  const [allTenders, setAllTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenders, setTenders] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [validationOptions, setValidationOptions] = useState({});
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [tenderLogs, setTenderLogs] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const deferredSearch = useDeferredValue(searchInput);
  const [filters, setFilters] = useState({ status: '', ministry: '', bidType: '' });
  const [editFiles, setEditFiles] = useState({});

  // ✅ ADDED: full log column selection + diff toggle
  const [logVisibleColumns, setLogVisibleColumns] = useState([]);
  const [logColAnchorEl, setLogColAnchorEl] = useState(null);
  const [showOnlyChanges, setShowOnlyChanges] = useState(true);

  const dataUrl = '/api/tenders';

  useEffect(() => {
    fetch(dataUrl)
      .then(res => res.json())
      .then(data => {
        const filteredData = user.role === 'End User'
          ? data.filter(row => row['Owner'] === user.username)
          : data;

        setAllTenders(filteredData);

        const deduped = latestCrmRows(
          filteredData,
          row => row['Bid Number'],
          ['Tender Updated Time']
        );
        setTenders(deduped);
        setVisibleColumns(
          JSON.parse(localStorage.getItem(`visibleColumns-v2-${user.username}-tenders`)) ||
          ['Bid Number', 'Tender Status', 'Ministry/State Name', 'Bid Type', 'Owner'].filter((key) => Object.keys(deduped[0] || {}).includes(key))
        );
        setLoading(false);
      })
      .catch(err => {
        console.error('❌ Tender data fetch error:', err);
        setLoading(false);
      });

    fetch(dataUrl + '?action=dropdowns')
      .then(res => res.json())
      .then(setValidationOptions)
      .catch(err => console.error('❌ Dropdown fetch error:', err));
  }, [user.username, user.role]);

  // ✅ missing handler (your code calls handleFilterChange but it wasn't defined)
  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => {
      if (filterKey === 'Tender Status') return { ...prev, status: value };
      if (filterKey === 'Ministry/State Name') return { ...prev, ministry: value };
      if (filterKey === 'Bid Type') return { ...prev, bidType: value };
      return prev;
    });
  };

  const handleColumnToggle = (col) => {
    setVisibleColumns(prev => {
      const updated = prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col];
      localStorage.setItem(`visibleColumns-v2-${user.username}-tenders`, JSON.stringify(updated));
      return updated;
    });
  };

  // ✅ log column toggle
  const handleLogColumnToggle = (col) => {
    setLogVisibleColumns(prev => {
      const updated = prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col];
      localStorage.setItem(`logVisibleColumns-${user.username}-tenders`, JSON.stringify(updated));
      return updated;
    });
  };

  // ✅ detect date fields
  const isDateField = (fieldName) => /date|validity/i.test(fieldName);
  const isThirdPartyRow = (row) => row?.['Tender Status'] === THIRD_PARTY_STATUS;
  const shouldShowEditField = (field) => !THIRD_PARTY_FIELDS.includes(field) || isThirdPartyRow(editRow);

  // ✅ UPDATED: One formatter for all cell values (fixes ISO date strings too)
  const formatValue = (key, value) => {
    if (value === null || value === undefined) return '';

    // If GAS returns Date objects (rare in JSON), handle
    if (Object.prototype.toString.call(value) === '[object Date]') {
      const d = new Date(value);
      if (!isNaN(d)) {
        if (key === 'Timestamp') {
          return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
        }
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      }
    }

    const str = String(value).trim();
    if (!str) return '';

    // Timestamp: prefer date+time
    if (key === 'Timestamp') {
      const d = new Date(str);
      if (!isNaN(d)) {
        return d.toLocaleString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
          hour12: false
        });
      }
      return str;
    }

    // ISO date string like 2025-07-10T18:30:00.000Z
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(str)) {
      const d = new Date(str);
      if (!isNaN(d)) {
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      }
    }

    // If the column name looks like a date field, try to parse more aggressively
    if (isDateField(key)) {
      // dd/MM/yyyy (your standard)
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
        const d = dayjs(str, 'DD/MM/YYYY', true);
        if (d.isValid()) return d.format('DD MMM YYYY');
      }

      // yyyy-MM-dd (common)
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const d = dayjs(str, 'YYYY-MM-DD', true);
        if (d.isValid()) return d.format('DD MMM YYYY');
      }

      // try Date constructor
      const d2 = new Date(str);
      if (!isNaN(d2)) {
        return d2.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      }
    }

    return str;
  };

  const filteredTenders = useMemo(() => tenders.filter(row => (
      (!filters.status || row['Tender Status'] === filters.status) &&
      (!filters.ministry || row['Ministry/State Name'] === filters.ministry) &&
      (!filters.bidType || row['Bid Type'] === filters.bidType) &&
      Object.values(row).some(val => (val || '').toString().toLowerCase().includes(deferredSearch.toLowerCase()))
    )), [tenders, filters, deferredSearch]);

  const unique = (key) => [...new Set(tenders.map(d => d[key]).filter(Boolean))];

  const fileToBase64 = (file, label) => {
    if (!file) return Promise.resolve(null);

    if (file.size > MAX_UPLOAD_BYTES) {
      return Promise.resolve({
        name: file.name,
        type: file.type || 'application/octet-stream',
        base64: '',
        size: file.size,
        tooLarge: true,
        label
      });
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const res = String(reader.result || '');
        const base64 = res.split('base64,')[1] || '';
        resolve({
          name: file.name,
          type: file.type || 'application/octet-stream',
          base64,
          size: file.size || 0,
          tooLarge: false,
          label
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleEditSubmit = async () => {
    const certObj = await fileToBase64(editFiles[AUTH_CERT_FIELD], AUTH_CERT_FIELD);
    if (certObj?.tooLarge) {
      alert('❌ Authorization Certificate is too large. Please upload a file under 1.2 MB.');
      return;
    }

    const updated = {
      ...editRow,
      'Tender Updated Time': new Date().toLocaleString('en-GB', { hour12: false })
    };

    if (updated['Tender Status'] === THIRD_PARTY_STATUS) {
      const missingField = THIRD_PARTY_FIELDS
        .filter(field => field !== AUTH_CERT_FIELD)
        .find(field => !String(updated[field] || '').trim());

      if (missingField) {
        alert(`❌ ${missingField} is required for Third Party Transfer.`);
        return;
      }

      if (!certObj && !String(updated[AUTH_CERT_FIELD] || '').trim()) {
        alert('❌ Authorization Certificate is required for Third Party Transfer.');
        return;
      }

      if (certObj) updated[AUTH_CERT_FIELD] = certObj;
    } else {
      THIRD_PARTY_FIELDS.forEach(field => {
        updated[field] = '';
      });
    }

    try {
      await fetch(dataUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated)
      }).then(async response => {
        if (!response.ok) throw new Error(await response.text());
      });
      setAllTenders(prev => [...prev, updated]);
      setTenders(prev => latestCrmRows(
        [...prev, updated],
        row => row['Bid Number'],
        ['Tender Updated Time']
      ));
      alert('✅ Tender updated successfully');
      setEditRow(null);
      setEditFiles({});
    } catch {
      alert('❌ Error updating tender');
    }
  };

  // logs compute columns & sort
  const handleViewLogs = (row) => {
    const key = row['Bid Number'];
    const logs = newestCrmRows(
      allTenders.filter(item => item['Bid Number'] === key),
      ['Tender Updated Time']
    );

    setTenderLogs(logs);

    const allCols = logs.length ? Object.keys(logs[logs.length - 1]) : [];
    const saved = JSON.parse(localStorage.getItem(`logVisibleColumns-${user.username}-tenders`));
    setLogVisibleColumns(saved || allCols);

    setShowOnlyChanges(true);
    setLogsOpen(true);
  };

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setEditRow(prev => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (field) => (e) => {
    const file = e.target.files?.[0] || null;
    setEditFiles(prev => ({ ...prev, [field]: file }));
  };

  // date picker handler for edit modal
  const handleEditDateChange = (field, newValue) => {
    const formatted = newValue ? dayjs(newValue).format('DD/MM/YYYY') : '';
    setEditRow(prev => ({ ...prev, [field]: formatted }));
  };

  // compute per-row changed fields vs previous row
  const computeChanges = (prevRow, currRow, cols) => {
    if (!prevRow) return cols;
    return cols.filter((col) => {
      const a = (prevRow?.[col] ?? '').toString();
      const b = (currRow?.[col] ?? '').toString();
      return a !== b;
    });
  };

  if (loading) return <LoadingOverlay />;

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box p={4}>
          <Box className="crm-page-header" display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <img className="crm-primary-logo" src="/assets/rido-sports-logo.png" alt="Rido Sports" />
            <Typography variant="h5" fontWeight="bold">Tender Records</Typography>
          </Box>

          <Box className="crm-filter-bar" display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
            <Box display="flex" alignItems="center">
              <TextField
                size="small"
                label="Search"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                sx={{ minWidth: 240 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                }}
              />
            </Box>

            {['Tender Status', 'Ministry/State Name', 'Bid Type'].map(filterKey => (
              <FormControl size="small" sx={{ minWidth: 160 }} key={filterKey}>
                <InputLabel>{filterKey}</InputLabel>
                <Select
                  value={filters[filterKey === 'Tender Status' ? 'status' : filterKey === 'Ministry/State Name' ? 'ministry' : 'bidType']}
                  label={filterKey}
                  onChange={e => handleFilterChange(filterKey, e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {unique(filterKey).map(item => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}

            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <ViewColumnIcon />
            </IconButton>
            <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
              <Box padding={2} sx={selectorStyle}>
                <Button
                  size="small"
                  onClick={() => {
                    if (!tenders[0]) return;
                    const cols = Object.keys(tenders[0]);
                    setVisibleColumns(cols);
                    localStorage.setItem(`visibleColumns-v2-${user.username}-tenders`, JSON.stringify(cols));
                  }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setVisibleColumns([]);
                    localStorage.setItem(`visibleColumns-v2-${user.username}-tenders`, JSON.stringify([]));
                  }}
                >
                  Deselect All
                </Button>
                {Object.keys(tenders[0] || {}).map(col => (
                  <Box key={col}>
                    <Checkbox
                      size="small"
                      checked={visibleColumns.includes(col)}
                      onChange={() => handleColumnToggle(col)}
                    /> {col}
                  </Box>
                ))}
              </Box>
            </Popover>
          </Box>

          <TableContainer component={Paper} className="crm-table-shell" variant="outlined">
            <Table size="small" sx={CRM_TABLE_SX}>
              <TableHead>
                <TableRow style={{ backgroundColor: '#6495ED' }}>
                  {visibleColumns.map(header => (
                    <TableCell key={header} style={{ color: 'white', cursor: 'pointer' }}>{header}</TableCell>
                  ))}
                  <TableCell style={{ color: 'white' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTenders.map((row, index) => (
                  <TableRow key={index}>
                    {visibleColumns.map((key, i) => (
                      <TableCell key={i}>
                        {formatValue(key, row[key])}
                      </TableCell>
                    ))}
                    <TableCell>
                      <IconButton onClick={() => setViewRow(row)}><VisibilityIcon /></IconButton>
                      <IconButton onClick={() => setEditRow(row)}><EditIcon /></IconButton>
                      <IconButton onClick={() => handleViewLogs(row)}><HistoryIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* View Modal */}
          <Dialog open={!!viewRow} onClose={() => setViewRow(null)} maxWidth="md" fullWidth>
            <DialogTitle>View Tender</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {viewRow && Object.entries(viewRow).map(([key, value]) => (
                  <Grid item xs={6} key={key}>
                    <TextField
                      fullWidth
                      size="small"
                      label={key}
                      value={formatValue(key, value)}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                ))}
              </Grid>
            </DialogContent>
          </Dialog>

          {/* View Logs */}
          <Dialog open={logsOpen} onClose={() => setLogsOpen(false)} maxWidth="xl" fullWidth>
            <DialogTitle>Tender Logs</DialogTitle>
            <DialogContent dividers>
              {/* Controls */}
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      checked={showOnlyChanges}
                      onChange={(e) => setShowOnlyChanges(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography sx={{ fontSize: 10 }}>Show only changed fields</Typography>}
                />

                <Button
                  size="small"
                  startIcon={<ViewColumnIcon />}
                  onClick={(e) => setLogColAnchorEl(e.currentTarget)}
                  variant="outlined"
                  sx={{ fontSize: 10 }}
                >
                  Columns
                </Button>

                <Popover
                  open={Boolean(logColAnchorEl)}
                  anchorEl={logColAnchorEl}
                  onClose={() => setLogColAnchorEl(null)}
                >
                  <Box padding={2} sx={selectorStyle} minWidth={260}>
                    <Button
                      size="small"
                      onClick={() => {
                        const cols = tenderLogs.length ? Object.keys(tenderLogs[tenderLogs.length - 1]) : [];
                        setLogVisibleColumns(cols);
                        localStorage.setItem(`logVisibleColumns-${user.username}-tenders`, JSON.stringify(cols));
                      }}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setLogVisibleColumns([]);
                        localStorage.setItem(`logVisibleColumns-${user.username}-tenders`, JSON.stringify([]));
                      }}
                    >
                      Deselect All
                    </Button>

                    {(tenderLogs.length ? Object.keys(tenderLogs[tenderLogs.length - 1]) : []).map(col => (
                      <Box key={col}>
                        <Checkbox
                          size="small"
                          checked={logVisibleColumns.includes(col)}
                          onChange={() => handleLogColumnToggle(col)}
                        /> {col}
                      </Box>
                    ))}
                  </Box>
                </Popover>
              </Box>

              {/* Full logs table with simple diff (no bold everywhere) */}
              <Table size="small" sx={{ ...CRM_TABLE_SX, ...logTableSx }}>
                <TableHead>
                  <TableRow style={{ backgroundColor: '#f7f9ff' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Version</TableCell>
                    {logVisibleColumns.map(col => (
                      <TableCell key={col} sx={{ fontWeight: 700 }}>
                        {col}
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 700 }}>Changed Fields</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {tenderLogs.map((log, idx) => {
                    const prev = tenderLogs[idx + 1] || null;
                    const changedCols = computeChanges(prev, log, logVisibleColumns);

                    return (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontSize: 'var(--crm-table-font-size, 10px)' }}>{idx + 1}</TableCell>

                        {logVisibleColumns.map(col => {
                          const isChanged = changedCols.includes(col);
                          const value = formatValue(col, log[col]);

                          // If showOnlyChanges is ON, blank out unchanged fields (but keep columns)
                          const displayValue = showOnlyChanges && prev && !isChanged ? '' : (value ?? '');

                          return (
                            <TableCell
                              key={col}
                              sx={
                                isChanged
                                  ? {
                                      borderLeft: '3px solid #6495ED',
                                      backgroundColor: '#f0f6ff' // subtle highlight, no bold
                                    }
                                  : undefined
                              }
                            >
                              {displayValue}
                            </TableCell>
                          );
                        })}

                        <TableCell sx={{ fontSize: 'var(--crm-table-font-size, 10px)' }}>
                          {prev ? changedCols.join(', ') : 'Initial Entry'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </DialogContent>
          </Dialog>

          {/* Edit Modal */}
          <Dialog open={!!editRow} onClose={() => { setEditRow(null); setEditFiles({}); }} maxWidth="md" fullWidth>
            <DialogTitle>Edit Tender</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {editRow && Object.keys(editRow).filter(shouldShowEditField).map((key, i) => (
                  <Grid item xs={6} key={i}>
                    {key === AUTH_CERT_FIELD ? (
                      <Button variant="outlined" component="label" fullWidth sx={{ justifyContent: 'flex-start', py: 1 }}>
                        {editFiles[key]?.name || editRow[key] || key}
                        <input hidden type="file" onChange={handleEditFileChange(key)} />
                      </Button>
                    ) : validationOptions[key] ? (
                      <FormControl fullWidth size="small">
                        <InputLabel>{key}</InputLabel>
                        <Select
                          name={key}
                          value={editRow[key] || ''}
                          onChange={handleUpdateChange}
                          label={key}
                        >
                          {validationOptions[key].map((opt, idx) => (
                            <MenuItem key={idx} value={opt}>{opt}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : isDateField(key) ? (
                      <DatePicker
                        label={key}
                        value={editRow[key] ? dayjs(editRow[key], 'DD/MM/YYYY') : null}
                        onChange={(newValue) => handleEditDateChange(key, newValue)}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small"
                          }
                        }}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        label={key}
                        name={key}
                        value={editRow[key] || ''}
                        onChange={handleUpdateChange}
                        size="small"
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button variant="contained" sx={{ backgroundColor: '#6495ED' }} onClick={handleEditSubmit}>
                  Save Changes
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default TenderTable;
